import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

import type {
  Account,
  Budget,
  Category,
  Currency,
  FinanceCategoryKind,
  FxRate,
  Transaction,
} from './types';

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

function parseErrorMessage(text: string, status: number): string {
  try {
    const body = JSON.parse(text) as { error?: { message?: string } };
    if (body.error?.message) {
      return body.error.message;
    }
  } catch {
    // fall through
  }
  return text || `Request failed (${status})`;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
  return res.json() as Promise<T>;
}

async function expectOk(res: Response): Promise<void> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
}

// --- Currencies ---

export async function fetchCurrencies(): Promise<Currency[]> {
  const res = await apiFetch('/rpc/finance/currencies');
  return (await parseJson<{ currencies: Currency[] }>(res)).currencies;
}

export async function createCurrency(input: {
  code: string;
  symbol?: string | null;
}): Promise<Currency> {
  const res = await apiFetch('/rpc/finance/currencies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ currency: Currency }>(res)).currency;
}

export async function updateCurrency(code: string, symbol: string | null): Promise<Currency> {
  const res = await apiFetch(`/rpc/finance/currencies/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    body: JSON.stringify({ symbol }),
  });
  return (await parseJson<{ currency: Currency }>(res)).currency;
}

export async function deleteCurrency(code: string): Promise<void> {
  await expectOk(
    await apiFetch(`/rpc/finance/currencies/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  );
}

// --- Accounts ---

export type AccountInput = {
  name: string;
  ownerMemberId?: string | null;
  institution?: string | null;
  accountNumber?: string | null;
  currencyCode: string;
  isActive?: boolean;
  sortOrder?: number;
  notes?: string | null;
};

export async function fetchAccounts(): Promise<Account[]> {
  const res = await apiFetch('/rpc/finance/accounts');
  return (await parseJson<{ accounts: Account[] }>(res)).accounts;
}

export async function createAccount(input: AccountInput): Promise<Account> {
  const res = await apiFetch('/rpc/finance/accounts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ account: Account }>(res)).account;
}

export async function updateAccount(id: number, patch: Partial<AccountInput>): Promise<Account> {
  const res = await apiFetch(`/rpc/finance/accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return (await parseJson<{ account: Account }>(res)).account;
}

export async function deleteAccount(id: number): Promise<void> {
  await expectOk(await apiFetch(`/rpc/finance/accounts/${id}`, { method: 'DELETE' }));
}

// --- FX rates ---

export async function fetchFxRates(from: string, to: string): Promise<FxRate[]> {
  const res = await apiFetch(
    `/rpc/finance/fx-rates?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
  return (await parseJson<{ rates: FxRate[] }>(res)).rates;
}

export async function putFxRates(
  month: string,
  rates: { currencyCode: string; rateToUsd: number }[],
): Promise<FxRate[]> {
  const res = await apiFetch('/rpc/finance/fx-rates', {
    method: 'PUT',
    body: JSON.stringify({ month, rates }),
  });
  return (await parseJson<{ rates: FxRate[] }>(res)).rates;
}

export type FxFetchSummary = {
  month: string;
  date: string;
  updated: { currencyCode: string; rateToUsd: number }[];
  skippedManual: string[];
  missing: string[];
};

/** Trigger the server-side Frankfurter fetch for a month range; manual rows are preserved. */
export async function fetchFxFromApi(input: {
  from: string;
  to: string;
}): Promise<FxFetchSummary[]> {
  const res = await apiFetch('/rpc/finance/fx-rates/fetch', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ results: FxFetchSummary[] }>(res)).results;
}

// --- Categories ---

export type CategoryInput = {
  parentId?: number | null;
  name: string;
  kind?: FinanceCategoryKind;
  isActive?: boolean;
  sortOrder?: number;
};

export async function fetchCategories(): Promise<Category[]> {
  const res = await apiFetch('/rpc/finance/categories');
  return (await parseJson<{ categories: Category[] }>(res)).categories;
}

export async function createCategory(input: CategoryInput): Promise<Category> {
  const res = await apiFetch('/rpc/finance/categories', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ category: Category }>(res)).category;
}

export async function updateCategory(id: number, patch: Partial<CategoryInput>): Promise<Category> {
  const res = await apiFetch(`/rpc/finance/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return (await parseJson<{ category: Category }>(res)).category;
}

export async function deleteCategory(id: number): Promise<void> {
  await expectOk(await apiFetch(`/rpc/finance/categories/${id}`, { method: 'DELETE' }));
}

export type CategoryUsage = {
  transactionCount: number;
  childCount: number;
};

export async function fetchCategoryUsage(id: number): Promise<CategoryUsage> {
  const res = await apiFetch(`/rpc/finance/categories/${id}/usage`);
  return parseJson<CategoryUsage>(res);
}

// --- Transactions ---

export type TransactionInput = {
  date: string;
  accountId?: number | null;
  toAccountId?: number | null;
  categoryId?: number | null;
  currencyCode: string;
  amount: number;
  notes?: string | null;
};

export type TransactionFilters = {
  month?: string;
  from?: string;
  to?: string;
  accountId?: number | number[];
  categoryId?: number | number[];
  q?: string;
  cursor?: string;
  limit?: number;
};

function appendIds(params: URLSearchParams, key: string, value: number | number[] | undefined) {
  if (value == null) return;
  const ids = Array.isArray(value) ? value : [value];
  for (const id of ids) params.append(key, String(id));
}

export async function fetchTransactions(
  filters: TransactionFilters = {},
): Promise<{ transactions: Transaction[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (filters.month) params.set('month', filters.month);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  appendIds(params, 'accountId', filters.accountId);
  appendIds(params, 'categoryId', filters.categoryId);
  if (filters.q) params.set('q', filters.q);
  if (filters.cursor != null) params.set('cursor', String(filters.cursor));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  const query = params.toString();
  const res = await apiFetch(`/rpc/finance/transactions${query ? `?${query}` : ''}`);
  return parseJson<{ transactions: Transaction[]; nextCursor: string | null }>(res);
}

function monthsCovering(from: string, to: string): string[] {
  const months: string[] = [];
  let year = Number(from.slice(0, 4));
  let month = Number(from.slice(5, 7));
  const endYear = Number(to.slice(0, 4));
  const endMonth = Number(to.slice(5, 7));
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month === 13) {
      month = 1;
      year += 1;
    }
    if (months.length > 36) break;
  }
  return months;
}

/** Transactions in a date window, one month at a time so a long statement still matches. */
export async function fetchTransactionsInRange(from: string, to: string): Promise<Transaction[]> {
  const transactions: Transaction[] = [];
  for (const month of monthsCovering(from, to)) {
    const page = await fetchTransactions({ month, limit: 500 });
    for (const transaction of page.transactions) {
      if (transaction.date >= from && transaction.date <= to) transactions.push(transaction);
    }
  }
  return transactions;
}

export async function fetchTransaction(id: number): Promise<Transaction> {
  const res = await apiFetch(`/rpc/finance/transactions/${id}`);
  return (await parseJson<{ transaction: Transaction }>(res)).transaction;
}

export async function createTransaction(input: TransactionInput): Promise<Transaction> {
  const res = await apiFetch('/rpc/finance/transactions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ transaction: Transaction }>(res)).transaction;
}

export async function updateTransaction(
  id: number,
  patch: Partial<TransactionInput>,
): Promise<Transaction> {
  const res = await apiFetch(`/rpc/finance/transactions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return (await parseJson<{ transaction: Transaction }>(res)).transaction;
}

export async function deleteTransaction(id: number): Promise<void> {
  await expectOk(await apiFetch(`/rpc/finance/transactions/${id}`, { method: 'DELETE' }));
}

const IMPORT_CHUNK = 500;

/** Bulk-create transactions in chunks; returns the total inserted. */
export async function importTransactions(rows: TransactionInput[]): Promise<number> {
  let imported = 0;
  for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
    const chunk = rows.slice(i, i + IMPORT_CHUNK);
    const res = await apiFetch('/rpc/finance/transactions/import', {
      method: 'POST',
      body: JSON.stringify({ transactions: chunk }),
    });
    imported += (await parseJson<{ imported: number }>(res)).imported;
  }
  return imported;
}

/** Gather every transaction matching `filters` by following the cursor (capped). */
export async function fetchAllTransactions(
  filters: Omit<TransactionFilters, 'cursor' | 'limit'> & { from?: string; to?: string } = {},
): Promise<Transaction[]> {
  const all: Transaction[] = [];
  const seen = new Set<number>();
  let cursor: string | undefined;
  for (let page = 0; page < 50; page += 1) {
    const params = new URLSearchParams();
    if (filters.month) params.set('month', filters.month);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    appendIds(params, 'accountId', filters.accountId);
    appendIds(params, 'categoryId', filters.categoryId);
    if (filters.q) params.set('q', filters.q);
    params.set('limit', '500');
    if (cursor != null) params.set('cursor', cursor);
    const res = await apiFetch(`/rpc/finance/transactions?${params.toString()}`);
    const data = await parseJson<{ transactions: Transaction[]; nextCursor: string | null }>(res);
    let added = 0;
    for (const tx of data.transactions) {
      if (seen.has(tx.id)) continue;
      seen.add(tx.id);
      all.push(tx);
      added += 1;
    }
    if (data.nextCursor == null || added === 0 || data.nextCursor === cursor) break;
    cursor = data.nextCursor;
  }
  return all;
}

// --- Budgets ---

export type BudgetInput = {
  month: string;
  categoryId: number;
  minUsd: number;
  maxUsd: number;
  notes?: string | null;
};

export async function fetchBudgets(month: string): Promise<Budget[]> {
  const res = await apiFetch(`/rpc/finance/budgets?month=${encodeURIComponent(month)}`);
  return (await parseJson<{ budgets: Budget[] }>(res)).budgets;
}

export async function putBudget(input: BudgetInput): Promise<Budget> {
  const res = await apiFetch('/rpc/finance/budgets', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return (await parseJson<{ budget: Budget }>(res)).budget;
}

export async function deleteBudget(month: string, categoryId: number): Promise<void> {
  await expectOk(
    await apiFetch(
      `/rpc/finance/budgets?month=${encodeURIComponent(month)}&categoryId=${categoryId}`,
      { method: 'DELETE' },
    ),
  );
}
